/**
 * AI メニュー開発 チャット画面 (会社管理者向け)。
 * 詳細: docs/design/v02-ai-menu-development-requirements.md §7-1
 * v0.3 (S-8/S-9/S-10): 業態対応・右パネル(ContextPanel)・構造化提案カード(StructuredProposalCard)
 *   ・tool_use ストリーミング対応を追加。詳細: docs/design/v03-ai-menu-dev-improvements.md §6
 *
 * v0.2 スコープ簡略化メモ: 「お気に入り追加」は AI 応答からの構造化データ抽出を行わず、
 * 応答テキスト全文を proposal_data.raw_text として保存する簡易実装 (F-107 の MVP版)。
 * v0.3 で propose_menu の構造化データ (structured) も併せて保存するようになった (§7.1)。
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Send, Plus, Loader2, ChefHat, Calculator, Wand2, Trash2, Square, SlidersHorizontal } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { postSSE } from '@/lib/sse';
import { toast } from '@/stores/toast';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { isNearBottom, isAwaitingGivenUp } from '@/lib/chat-scroll';
import { ResponseMissingNotice } from '@/components/ai-menu/ResponseMissingNotice';
import { ContextPanel, type ContextSlots, type MenuCategoryOption } from '@/components/ai-menu/ContextPanel';
import { shouldAutoApplyIndustry } from '@/lib/ai-menu-industry-select';
import { ProposalGroup, type ProposalEntry } from '@/components/ai-menu/ProposalGroup';
import type { StructuredProposal } from '@/components/ai-menu/StructuredProposalCard';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type ChatSession = { session_id: string; title: string | null; started_by: string | null; created_at: string; updated_at: string };
type ContentBlock = { type: string; text?: string; id?: string; name?: string; input?: unknown };
type ChatMessage = {
  message_id: string; role: 'user' | 'assistant' | 'tool_use' | 'tool_result'; content: ContentBlock[];
  structured?: StructuredProposal; created_at: string;
};
type IndustryTemplateSummary = { template_id: string; template_name: string } | null;
type SessionDetail = {
  session_id: string; title: string | null; status: string; focus_profile_id: string | null;
  context_slots: ContextSlots; target_category_name: string | null; industry_template: IndustryTemplateSummary;
  messages: ChatMessage[];
};
type Profile = { profile_id: string; profile_name: string; stores: Array<{ store_id: string; store_name: string }> };
type IndustryTemplate = {
  template_id: string; template_name: string; description: string | null;
  ingredient_groups: Array<{ group_name: string; display_order: number }>;
  recipe_sections: Array<{ section_name: string; display_order: number }>;
};
// v0.3 業態初期表示自動選択 (migration 0033): GET /menu-dev/default-industry のレスポンス
type DefaultIndustryResponse = {
  template_id: string | null;
  source: 'primary_assignment' | 'unanimous_assignments' | 'none';
  source_node_name: string | null;
  reason: string;
};

const QUICKSTARTS = [
  { label: '新メニュー提案', icon: ChefHat, template: '新メニューを提案してください。カテゴリ: 、使用したい食材: 、季節: 、目標価格: ' },
  { label: '原価シミュレーション', icon: Calculator, template: '以下の食材と分量で1人前の原価をシミュレーションしてください。\n食材と分量: \n目標販売価格 または 目標原価率: ' },
  { label: '既存メニュー改良', icon: Wand2, template: '「」の改良案を、原価率改善・客単価向上・付加価値の3観点で提案してください。' },
];

const textOf = (content: ContentBlock[]): string =>
  content.filter((b) => b.type === 'text' && b.text).map((b) => b.text).join('\n');

/** 連続する role='tool_use' メッセージを1応答分のグループとしてまとめる (1 tool_use = 1 message行、追加要件§1.5) */
const groupProposalMessages = (messages: ChatMessage[]): Array<{ index: number; entries: ProposalEntry[] }> => {
  const groups: Array<{ index: number; entries: ProposalEntry[] }> = [];
  let current: ProposalEntry[] = [];
  let startIndex = -1;
  messages.forEach((m, idx) => {
    if (m.role === 'tool_use' && m.structured) {
      const toolUseId = m.content.find((b) => b.type === 'tool_use')?.id ?? m.message_id;
      if (current.length === 0) startIndex = idx;
      current.push({ toolUseId, messageId: m.message_id, proposal: m.structured });
    } else if (current.length > 0) {
      groups.push({ index: startIndex, entries: current });
      current = [];
    }
  });
  if (current.length > 0) groups.push({ index: startIndex, entries: current });
  return groups;
};

export function AiMenuChatPage() {
  const qc = useQueryClient();
  // ⚠️ ai_menu_chat:manageを持っていてもai_menu_favorites:manageを持たない権限構成があり得る
  // (POST /menu-favoritesは別feature、§8-184)。お気に入りボタン(個別/一括)はこちらで出し分ける。
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canFavorite = hasPermissionForFeatureAction('ai_menu_favorites', 'manage');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [streamingProposals, setStreamingProposals] = useState<ProposalEntry[]>([]);
  const [sending, setSending] = useState(false);
  const [newSessionProfileId, setNewSessionProfileId] = useState('');
  const [newSessionIndustryId, setNewSessionIndustryId] = useState('');
  const [favoriteModal, setFavoriteModal] = useState<{ messageId: string; menuName: string; content: string; originalContent: string; structured?: StructuredProposal } | null>(null);
  const [deletingSession, setDeletingSession] = useState<ChatSession | null>(null);
  const [contextPanelOpenMobile, setContextPanelOpenMobile] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);
  const [favoritedMessageIds, setFavoritedMessageIds] = useState<Set<string>>(new Set());
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const lastSessionIdRef = useRef<string | null>(null);
  const stickToBottomRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  // Design相談反映 (2026-08-04): 生成中インジケーターに経過時間を表示する (CLAUDE.md §8-37の
  // 「経過時間表示」パターンをAI-OCRから横展開)。このタブ自身が送信した分は正確な開始時刻を、
  // 別タブ/リロード後に「最後の発言がuserのまま」を検知したケースはそのuser発言のcreated_atを使う
  const sendStartedAtRef = useRef<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const { data: sessions = [] } = useQuery<ChatSession[]>({
    queryKey: ['ai-menu-sessions'],
    queryFn: () => apiFetch<ChatSession[]>('/menu-chat/sessions'),
  });

  const { data: profiles = [] } = useQuery<Profile[]>({
    queryKey: ['signature-profiles'],
    queryFn: () => apiFetch<Profile[]>('/menu-dev/profiles'),
  });

  const { data: industryTemplates = [] } = useQuery<IndustryTemplate[]>({
    queryKey: ['menu-dev-industry-templates'],
    queryFn: () => apiFetch<IndustryTemplate[]>('/menu-dev/industry-templates'),
    staleTime: Infinity,
  });

  // v0.3 業態初期表示自動選択 (migration 0033): ログインユーザーの主所属から推定した業態。
  // staleTime 5分 + NodesPage側の保存時invalidateQueriesで即時反映を両立する (user Q5確定)
  const { data: defaultIndustry } = useQuery<DefaultIndustryResponse>({
    queryKey: ['ai-menu-default-industry'],
    queryFn: () => apiFetch<DefaultIndustryResponse>('/menu-dev/default-industry'),
    staleTime: 5 * 60 * 1000,
  });
  const [autoSelectedFromScope, setAutoSelectedFromScope] = useState(false);
  const autoAppliedRef = useRef(false);

  useEffect(() => {
    const shouldApply = shouldAutoApplyIndustry({
      sessionId,
      autoAlreadyApplied: autoAppliedRef.current,
      manuallySelectedIndustryId: newSessionIndustryId,
      defaultIndustryTemplateId: defaultIndustry?.template_id,
      availableIndustryTemplateIds: industryTemplates.map((t) => t.template_id),
    });
    if (!shouldApply) return;
    setNewSessionIndustryId(defaultIndustry!.template_id!);
    setAutoSelectedFromScope(true);
    autoAppliedRef.current = true;
  }, [defaultIndustry, industryTemplates, sessionId, newSessionIndustryId]);

  const onIndustryChange = (v: string) => {
    setNewSessionIndustryId(v);
    setAutoSelectedFromScope(false);
  };

  /** 「新しい話題」開始時の共通リセット。autoAppliedRef もクリアし、次の新規セッションで
   *  業態自動選択の useEffect が再度評価されるようにする */
  const startNewTopic = () => {
    setSessionId(null);
    setStreamingText('');
    setNewSessionProfileId('');
    setNewSessionIndustryId('');
    setAutoSelectedFromScope(false);
    autoAppliedRef.current = false;
  };

  const { data: categories = [] } = useQuery<MenuCategoryOption[]>({
    queryKey: ['menu-categories'],
    queryFn: () => apiFetch<Array<{ menu_category_id: string; category_name: string; effective_to: string | null }>>('/menu-categories')
      .then((rows) => rows.filter((r) => r.effective_to === null).map((r) => ({ menu_category_id: r.menu_category_id, category_name: r.category_name }))),
  });

  const { data: sessionDetail, refetch: refetchDetail } = useQuery<SessionDetail>({
    queryKey: ['ai-menu-session', sessionId],
    queryFn: () => apiFetch<SessionDetail>(`/menu-chat/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
    refetchInterval: (query) => {
      if (sending) return false;
      const messages = query.state.data?.messages;
      if (!messages?.length) return false;
      if (isAwaitingGivenUp(messages.at(-1), Date.now())) return false;
      return messages.at(-1)?.role === 'user' ? 4000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const selectedIndustryTemplate = industryTemplates.find((t) => t.template_id === (sessionDetail?.industry_template?.template_id ?? newSessionIndustryId));
  const industryGroups = selectedIndustryTemplate?.ingredient_groups.sort((a, b) => a.display_order - b.display_order).map((g) => g.group_name);
  const industrySections = selectedIndustryTemplate?.recipe_sections.sort((a, b) => a.display_order - b.display_order).map((s) => s.section_name);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      stickToBottomRef.current = isNearBottom(container.scrollHeight, container.scrollTop, container.clientHeight);
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [sessionId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isNewSession = lastSessionIdRef.current !== sessionId;
    lastSessionIdRef.current = sessionId;
    if (isNewSession) { stickToBottomRef.current = true; setFavoritedMessageIds(new Set()); }
    if (stickToBottomRef.current) container.scrollTop = container.scrollHeight;
  }, [sessionId, sessionDetail?.messages.length, streamingText, streamingProposals.length]);

  const favoriteMut = useMutation({
    mutationFn: (params: { message_id: string; menu_name: string; content: string; original_content: string; structured?: StructuredProposal }) =>
      apiFetch('/menu-favorites', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          message_id: params.message_id,
          proposal_data: { menu_name: params.menu_name, content: params.content, original_content: params.original_content, structured: params.structured },
        }),
      }),
    onSuccess: (_data, params) => {
      toast.success('お気に入りに追加しました');
      setFavoritedMessageIds((prev) => new Set(prev).add(params.message_id));
      setFavoriteModal(null);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'お気に入り追加に失敗しました'),
  });

  const bulkFavoriteMut = useMutation({
    mutationFn: (entries: ProposalEntry[]) =>
      apiFetch<{ favorite_ids: string[]; proposal_ids: string[] }>('/menu-favorites/bulk', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          message_ids: entries.map((e) => e.messageId),
          proposals: entries.map((e) => ({
            message_id: e.messageId, tool_use_id: e.toolUseId, menu_name: e.proposal.menu_name,
            structured: e.proposal,
          })),
        }),
      }),
    onSuccess: (_data, entries) => {
      toast.success(`${entries.length}案をまとめてお気に入り登録しました`);
      setFavoritedMessageIds((prev) => { const next = new Set(prev); entries.forEach((e) => next.add(e.messageId)); return next; });
    },
    onError: (e) => {
      if (e instanceof ApiClientError && e.code === 'ALREADY_FAVORITED') {
        toast.error('一部の提案は既にお気に入り登録済みのため、一括登録を中断しました');
      } else {
        toast.error(e instanceof Error ? e.message : '一括登録に失敗しました');
      }
    },
  });

  const deleteSessionMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/menu-chat/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      toast.success('会話を削除しました');
      if (sessionId === id) {
        if (sending) abortRef.current?.abort();
        setSessionId(null);
        setStreamingText('');
      }
      qc.invalidateQueries({ queryKey: ['ai-menu-sessions'] });
      setDeletingSession(null);
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeletingSession(null); },
  });

  const runToolUseStream = (path: string, body: Record<string, unknown>, controller: AbortController) =>
    postSSE(path, body, {
      onEvent: (event, data) => {
        if (event === 'delta') {
          const parsed = JSON.parse(data) as { text: string };
          setStreamingText((prev) => prev + parsed.text);
        } else if (event === 'tool_use_complete') {
          const parsed = JSON.parse(data) as { tool_use_id: string; tool_name: string; input: StructuredProposal };
          if (parsed.tool_name === 'propose_menu') {
            setStreamingProposals((prev) => [...prev.filter((p) => p.toolUseId !== parsed.tool_use_id), {
              toolUseId: parsed.tool_use_id, messageId: parsed.tool_use_id, proposal: parsed.input,
            }]);
          }
        } else if (event === 'done') {
          setStreamingText('');
          setStreamingProposals([]);
          refetchDetail();
          qc.invalidateQueries({ queryKey: ['ai-menu-sessions'] });
        } else if (event === 'error') {
          const parsed = JSON.parse(data) as { message: string };
          toast.error(parsed.message);
          setStreamingText('');
          setStreamingProposals([]);
          refetchDetail();
        }
      },
    }, controller.signal);

  const send = async (content: string) => {
    if (!content.trim()) return;
    stickToBottomRef.current = true;
    let activeSessionId = sessionId;
    if (!activeSessionId) {
      const created = await apiFetch<{ session_id: string }>('/menu-chat/sessions', {
        method: 'POST',
        body: JSON.stringify({
          ...(newSessionProfileId ? { focus_profile_id: newSessionProfileId } : {}),
          ...(!newSessionProfileId && newSessionIndustryId ? { industry_template_id: newSessionIndustryId } : {}),
        }),
      });
      activeSessionId = created.session_id;
      setSessionId(activeSessionId);
      qc.invalidateQueries({ queryKey: ['ai-menu-sessions'] });
    }

    setInput('');
    setSending(true);
    sendStartedAtRef.current = Date.now();
    setStreamingText('');
    setStreamingProposals([]);
    setJustStopped(false);
    const controller = new AbortController();
    abortRef.current = controller;

    qc.setQueryData<SessionDetail | undefined>(['ai-menu-session', activeSessionId], (prev) => prev && ({
      ...prev,
      messages: [...prev.messages, { message_id: `optimistic-${Date.now()}`, role: 'user', content: [{ type: 'text', text: content }], created_at: new Date().toISOString() }],
    }));

    try {
      await runToolUseStream(`/menu-chat/sessions/${activeSessionId}/messages`, { content }, controller);
    } catch (e) {
      if (!controller.signal.aborted) {
        toast.error(e instanceof Error ? e.message : 'AI応答の取得に失敗しました');
        refetchDetail();
      }
    } finally {
      setSending(false);
    }
  };

  const regenerate = async (entry: ProposalEntry, servings: number) => {
    if (!sessionId) return;
    setRegeneratingMessageId(entry.messageId);
    sendStartedAtRef.current = Date.now();
    setStreamingText('');
    setStreamingProposals([]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await runToolUseStream(`/menu-chat/sessions/${sessionId}/messages/${entry.messageId}/regenerate`, { production_servings: servings }, controller);
    } catch (e) {
      if (!controller.signal.aborted) toast.error(e instanceof Error ? e.message : '再生成に失敗しました');
    } finally {
      setRegeneratingMessageId(null);
    }
  };

  const [justStopped, setJustStopped] = useState(false);
  const stop = () => {
    abortRef.current?.abort();
    setStreamingText('');
    setStreamingProposals([]);
    setJustStopped(true);
  };
  useEffect(() => {
    if (sessionDetail?.messages.at(-1)?.role === 'assistant' || sessionDetail?.messages.at(-1)?.role === 'tool_use') setJustStopped(false);
  }, [sessionDetail]);

  // Design相談反映 (2026-08-04): 生成中インジケーターの経過時間表示用。1秒ごとに再描画をトリガーする
  const lastMessage = sessionDetail?.messages.at(-1);
  const awaitingGivenUp = !sending && isAwaitingGivenUp(lastMessage, nowTick);
  const isAwaitingResponse = !justStopped && (sending || (lastMessage?.role === 'user' && !awaitingGivenUp));
  useEffect(() => {
    if (!isAwaitingResponse) return;
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isAwaitingResponse]);
  const elapsedSec = (() => {
    if (!isAwaitingResponse) return null;
    const startedAt = sending && sendStartedAtRef.current
      ? sendStartedAtRef.current
      : (lastMessage?.role === 'user' ? new Date(lastMessage.created_at).getTime() : null);
    if (startedAt == null) return null;
    return Math.max(0, Math.floor((nowTick - startedAt) / 1000));
  })();
  const formatElapsed = (sec: number) => (sec < 60 ? `${sec}秒` : `${Math.floor(sec / 60)}分${sec % 60}秒`);

  const proposalGroups = sessionDetail ? groupProposalMessages(sessionDetail.messages) : [];
  const proposalGroupIndexSet = new Set(proposalGroups.map((g) => g.index));
  const filledSlotCount = sessionDetail ? Object.entries(sessionDetail.context_slots).filter(([k, v]) =>
    !['updated_by', 'updated_at', 'auto_mode'].includes(k) && v != null && v !== '').length : 0;

  const openFavoriteModal = (entry: ProposalEntry) => {
    const text = `${entry.proposal.menu_name}\n価格: ${entry.proposal.suggested_price_yen}円 / 原価率: ${entry.proposal.cost_ratio_percent.toFixed(1)}%`;
    setFavoriteModal({ messageId: entry.messageId, menuName: entry.proposal.menu_name, content: text, originalContent: text, structured: entry.proposal });
  };

  const contextPanel = sessionId && sessionDetail && (
    <ContextPanel
      sessionId={sessionId}
      contextSlots={sessionDetail.context_slots}
      industryTemplate={sessionDetail.industry_template}
      industryTemplates={industryTemplates.map((t) => ({ template_id: t.template_id, template_name: t.template_name }))}
      categories={categories}
      onPatched={() => refetchDetail()}
    />
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-4 h-[calc(100vh-140px)]">
      {/* セッション一覧 */}
      <div className="hidden lg:flex flex-col gap-2 overflow-y-auto">
        <button
          type="button"
          onClick={startNewTopic}
          className="inline-flex items-center gap-2 px-3 py-2 rounded bg-brand-red text-white text-sm font-medium"
        >
          <Plus size={16} /> 新しい話題
        </button>
        {sessions.map((s) => (
          <div
            key={s.session_id}
            className={`group flex items-center gap-1 rounded ${sessionId === s.session_id ? 'bg-brand-red/10' : 'bg-white border border-surface-border hover:bg-surface-sunken'}`}
          >
            <button
              type="button"
              onClick={() => setSessionId(s.session_id)}
              className={`flex-1 min-w-0 text-left px-3 py-2 text-sm truncate ${sessionId === s.session_id ? 'text-brand-red' : 'text-text-main'}`}
            >
              {s.title ?? '(無題の会話)'}
            </button>
            <button
              type="button"
              onClick={() => setDeletingSession(s)}
              className="shrink-0 p-2 text-zinc-300 hover:text-danger-text opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              aria-label={`「${s.title ?? '無題の会話'}」を削除`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* 会話ペイン */}
      <div className="flex flex-col border border-surface-border rounded-xl bg-white shadow-sm overflow-hidden min-w-0">
        <div className="px-4 py-3 border-b border-surface-line flex items-center gap-2 flex-wrap">
          <Sparkles size={18} className="text-brand-red shrink-0" />
          <span className="font-h3 text-h3 truncate">{sessionDetail?.title ?? 'AI メニュー開発'}</span>
          {!sessionId && (
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {industryTemplates.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select
                    value={newSessionProfileId ? 'none' : (newSessionIndustryId || 'none')}
                    onValueChange={(v) => onIndustryChange(v === 'none' ? '' : v)}
                    disabled={Boolean(newSessionProfileId)}
                  >
                    <SelectTrigger
                      aria-label="業態 (任意)"
                      aria-describedby={autoSelectedFromScope ? 'auto-industry-hint' : undefined}
                      className="min-h-[44px] sm:min-h-0 text-xs w-auto min-w-0 max-w-[45vw] sm:max-w-none"
                    >
                      <SelectValue className="truncate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">業態: 指定なし</SelectItem>
                      {industryTemplates.map((t) => (
                        <SelectItem key={t.template_id} value={t.template_id}>業態: {t.template_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {autoSelectedFromScope && (
                    // Design CRITICAL対応: hidden(display:none)はアクセシビリティツリーから除外され
                    // モバイルでaria-live/aria-describedbyがSRに読み上げられなくなるため、
                    // sr-only(視覚的に隠すのみ)+ sm:not-sr-only で視覚表示だけをブレークポイント制御する
                    <span
                      id="auto-industry-hint"
                      role="status"
                      aria-live="polite"
                      className="text-xs text-text-sub sr-only sm:not-sr-only sm:inline"
                    >
                      主所属から自動選択
                    </span>
                  )}
                </div>
              )}
              {profiles.length > 0 && (
                <select
                  value={newSessionProfileId}
                  onChange={(e) => setNewSessionProfileId(e.target.value)}
                  className="text-xs border border-surface-border rounded px-2 py-1 bg-white"
                  aria-label="対象プロファイル (任意)"
                >
                  <option value="">対象プロファイル: 全体</option>
                  {profiles.map((p) => (
                    <option key={p.profile_id} value={p.profile_id}>
                      対象プロファイル: {p.profile_name}{p.stores.length > 0 ? ` (${p.stores.map((s) => s.store_name).join('、')})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {sessionId && sessionDetail?.focus_profile_id && (
            <span className="ml-auto text-xs px-2 py-1 rounded-full bg-highlight-bg text-text-main border border-brand-gold/30">
              対象: {profiles.find((p) => p.profile_id === sessionDetail.focus_profile_id)?.profile_name ?? 'プロファイル選択中'}
            </span>
          )}
          {sessionId && sessionDetail && (
            <button
              type="button"
              onClick={() => setContextPanelOpenMobile(true)}
              className="lg:hidden inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-surface-border text-text-body"
            >
              <SlidersHorizontal size={13} /> 開発条件 ({filledSlotCount}項目)
            </button>
          )}
        </div>
        <div className="lg:hidden flex items-center gap-2 px-3 py-2 border-b border-surface-line bg-surface-sunken">
          <button
            type="button"
            onClick={startNewTopic}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-brand-red text-white text-xs font-medium shrink-0"
          >
            <Plus size={14} /> 新しい話題
          </button>
          {sessions.length > 0 && (
            <>
              <select
                value={sessionId ?? ''}
                onChange={(e) => setSessionId(e.target.value || null)}
                className="flex-1 min-w-0 border border-surface-border rounded px-2 py-1.5 text-xs bg-white"
                aria-label="過去の会話セッションを選択"
              >
                <option value="">(セッションを選択)</option>
                {sessions.map((s) => (
                  <option key={s.session_id} value={s.session_id}>{s.title ?? '(無題の会話)'}</option>
                ))}
              </select>
              {sessionId && (
                <button
                  type="button"
                  onClick={() => { const s = sessions.find((x) => x.session_id === sessionId); if (s) setDeletingSession(s); }}
                  className="shrink-0 p-2 text-zinc-400 hover:text-danger-text"
                  aria-label="選択中の会話を削除"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </>
          )}
        </div>

        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {(!sessionDetail || sessionDetail.messages.length === 0) && !streamingText && streamingProposals.length === 0 && (
            <div className="text-sm text-zinc-400 text-center py-8">
              新メニュー提案・原価シミュレーション・既存メニュー改良について、AIに相談してみましょう
            </div>
          )}
          {sessionDetail?.messages.map((m, idx) => {
            if (m.role === 'tool_use') {
              // このメッセージが属するグループの先頭でのみ ProposalGroup を1回描画する
              const group = proposalGroups.find((g) => g.index === idx);
              if (!group) return null;
              return (
                <ProposalGroup
                  key={`group-${group.index}`}
                  proposals={group.entries}
                  industryGroups={industryGroups}
                  industrySections={industrySections}
                  onFavorite={openFavoriteModal}
                  onRegenerate={regenerate}
                  regeneratingMessageId={regeneratingMessageId}
                  onBulkFavorite={() => bulkFavoriteMut.mutate(group.entries)}
                  bulkFavoritePending={bulkFavoriteMut.isPending}
                  registeredMessageIds={favoritedMessageIds}
                  canFavorite={canFavorite}
                />
              );
            }
            if (proposalGroupIndexSet.has(idx)) return null; // グループ内の後続行は上でまとめて描画済み
            const text = textOf(m.content);
            return (
              <div key={m.message_id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div className={`max-w-[80%] rounded-xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-brand-red text-white' : 'bg-surface-sunken text-text-main'}`}>
                  {text || (m.role === 'assistant' ? <span className="text-zinc-400 italic">(応答内容を取得できませんでした。もう一度質問してみてください)</span> : null)}
                </div>
              </div>
            );
          })}
          {streamingText && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-xl px-4 py-2 text-sm whitespace-pre-wrap bg-surface-sunken text-text-main" aria-live="polite">
                {streamingText}
              </div>
            </div>
          )}
          {streamingProposals.length > 0 && (
            <ProposalGroup
              proposals={streamingProposals}
              industryGroups={industryGroups}
              industrySections={industrySections}
              isGenerating
              onFavorite={() => { /* 生成中は保存不可、doneまで待つ */ }}
              onRegenerate={() => { /* 生成中は再生成不可 */ }}
            />
          )}
          {sending && !streamingText && streamingProposals.length === 0 && (
            <div className="flex justify-start items-center gap-2 text-xs text-zinc-400">
              <Loader2 size={16} className="animate-spin" />
              {elapsedSec != null && (
                <span className="tabular-nums">
                  生成中です (経過{formatElapsed(elapsedSec)}・通常1〜2分){elapsedSec > 90 ? ' もう少しお待ちください' : ''}
                </span>
              )}
            </div>
          )}
          {!sending && justStopped && (
            <div className="flex justify-start items-center gap-2 text-xs text-zinc-400">
              <Loader2 size={14} className="animate-spin" /> 停止処理中です…
            </div>
          )}
          {!sending && !justStopped && awaitingGivenUp && lastMessage && (
            <ResponseMissingNotice onResend={() => send(textOf(lastMessage.content))} />
          )}
          {!sending && !justStopped && !awaitingGivenUp && sessionDetail && sessionDetail.messages.length > 0 &&
            sessionDetail.messages.at(-1)?.role === 'user' && (
            <div className="flex justify-start items-center gap-2 text-xs text-zinc-400">
              <Loader2 size={14} className="animate-spin" />
              <span className="tabular-nums">
                AIが応答を生成中です{elapsedSec != null ? ` (経過${formatElapsed(elapsedSec)}・通常1〜2分)` : ''} (他の画面に移動しても生成は続きます)
                {elapsedSec != null && elapsedSec > 90 ? ' もう少しお待ちください' : ''}
              </span>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-surface-line space-y-2">
          <div className="flex gap-2 overflow-x-auto" role="group" aria-label="クイックスタート">
            {QUICKSTARTS.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => setInput(q.template)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-surface-border text-xs text-text-sub hover:bg-surface-sunken whitespace-nowrap shrink-0"
              >
                <q.icon size={13} /> {q.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-text-muted">
            新メニュー提案は3案生成のため通常1〜2分かかります(案数を絞ると高速化)
          </p>
          <div className="flex gap-2 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="メッセージを入力 (Enterで改行、Shift+Enterで送信)"
              rows={2}
              disabled={sending}
              className="flex-1 border border-surface-border rounded px-3 py-2 text-sm resize-none disabled:opacity-50"
            />
            {sending ? (
              <button
                type="button"
                onClick={stop}
                className="p-2.5 rounded bg-text-main text-white hover:bg-black"
                aria-label="生成を停止"
                title="生成を停止"
              >
                <Square size={18} fill="currentColor" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => send(input)}
                disabled={!input.trim()}
                className="p-2.5 rounded bg-brand-red text-white disabled:opacity-50"
                aria-label="送信"
              >
                <Send size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 右パネル: 開発条件 (デスクトップ常時表示) */}
      <div className="hidden lg:block border border-surface-border rounded-xl bg-white shadow-sm overflow-hidden">
        {contextPanel ?? (
          <div className="p-4 text-xs text-text-muted">会話を開始すると開発条件がここに表示されます</div>
        )}
      </div>

      {/* 右パネル: モバイル/タブレットはモーダル表示 */}
      <Modal
        open={contextPanelOpenMobile}
        title="開発条件"
        onClose={() => setContextPanelOpenMobile(false)}
        size="sm"
      >
        {contextPanel}
      </Modal>

      <Modal
        open={favoriteModal !== null}
        title="お気に入りに追加"
        onClose={() => setFavoriteModal(null)}
        footer={
          <>
            <button type="button" onClick={() => setFavoriteModal(null)} className="px-4 py-2 rounded border border-surface-border hover:bg-surface-sunken">キャンセル</button>
            <button
              type="button"
              disabled={favoriteMut.isPending || !favoriteModal?.menuName.trim() || !favoriteModal?.content.trim()}
              onClick={() => favoriteModal && favoriteMut.mutate({ message_id: favoriteModal.messageId, menu_name: favoriteModal.menuName.trim(), content: favoriteModal.content.trim(), original_content: favoriteModal.originalContent, structured: favoriteModal.structured })}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50"
            >
              {favoriteMut.isPending ? '保存中…' : '保存'}
            </button>
          </>
        }
      >
        {favoriteModal && (
          <div className="space-y-3">
            <p className="text-xs text-text-sub">
              1つの応答に複数の提案が含まれる場合は、下の本文を編集してこのメニューの部分だけを残してから保存してください
              (例：「案1」「案2」の2つが含まれる場合、保存したい方だけ残してもう一方の文章は削除する)。
              編集した内容は「編集済み」として記録され、お気に入り一覧からAIの元の回答も確認できます。
            </p>
            <div>
              <label className="block text-sm text-text-sub mb-1">メニュー名*</label>
              <input autoFocus value={favoriteModal.menuName}
                onChange={(e) => setFavoriteModal({ ...favoriteModal, menuName: e.target.value })}
                placeholder="例：焦がしねぎ豚骨ラーメン"
                className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">内容*</label>
              <textarea value={favoriteModal.content}
                onChange={(e) => setFavoriteModal({ ...favoriteModal, content: e.target.value })}
                rows={10}
                className="w-full px-3 py-2 rounded border border-surface-border focus-ring text-sm font-mono" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deletingSession !== null}
        title="会話を削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteSessionMut.isPending}
        onClose={() => { if (!deleteSessionMut.isPending) setDeletingSession(null); }}
        onConfirm={() => deletingSession && deleteSessionMut.mutate(deletingSession.session_id)}
        message={deletingSession && (
          <div className="space-y-2">
            <p>「<strong>{deletingSession.title ?? '無題の会話'}</strong>」を削除します。</p>
            <p>この会話とやり取りの履歴を削除します。この操作は取り消せません (お気に入りに保存済みの提案は削除されません)。</p>
          </div>
        )}
      />
    </div>
  );
}
